/**
 * Utilidades puras de voz (sin React) para las respuestas habladas de los
 * agentes (D-Talk / Mapa de Talento) y el modo voz de EPIC.
 *
 * La composición del dictado y la clasificación de errores viven en `shared/`
 * para testearse desde el backend (`npm run test:speech`); acá solo se
 * re-exportan para que el front siga importando de `@/lib/speech-utils`.
 */
export { composeTranscript, classifySpeechError, type SpeechErrorKind } from '../../../shared/speech-transcript';

/** Idioma del chat → voz Polly neural (backend/src/lib/polly-tts.ts). */
export const AGENT_VOICE: Record<'es' | 'pt' | 'en', string> = {
  es: 'Lupe',
  pt: 'Camila',
  en: 'Joanna',
};

/** Idioma del chat → BCP-47 para SpeechRecognition / speechSynthesis. */
export const STT_LANG: Record<'es' | 'pt' | 'en', string> = {
  es: 'es-AR',
  pt: 'pt-BR',
  en: 'en-US',
};

export interface AudioPlayback {
  /** Resuelve al terminar (o al cortarse). Nunca rechaza. */
  done: Promise<void>;
  stop: () => void;
}

/**
 * Reproduce audio base64 con posibilidad de corte. Patrón extraído de
 * ConversationalMode (base64 → Blob → <audio>), con revoke del object URL.
 */
export function createAudioPlayback(base64: string, mimeType: string): AudioPlayback {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: mimeType }));
  const audio = new Audio(url);

  let finish: () => void;
  const done = new Promise<void>((resolve) => {
    finish = resolve;
  });
  const cleanup = () => {
    URL.revokeObjectURL(url);
    finish();
  };
  audio.onended = cleanup;
  audio.onerror = cleanup;
  // NotAllowedError de autoplay (Safari) → terminar en silencio, sin crash
  audio.play().catch(cleanup);

  return {
    done,
    stop: () => {
      audio.pause();
      cleanup();
    },
  };
}

export interface ControllableAudio extends AudioPlayback {
  pause: () => void;
  resume: () => void;
  setRate: (rate: number) => void;
}

/**
 * Misma silueta que createAudioPlayback pero desde una URL (mp3 pregenerados,
 * p. ej. la voz en off del workshop del Vault), con pausa y velocidad. `done`
 * nunca rechaza: un 404 o el autoplay bloqueado resuelven enseguida y el
 * llamador decide (el workshop espera la duración estimada del subtítulo).
 */
export function createAudioPlaybackFromUrl(url: string, opts: { rate?: number } = {}): ControllableAudio {
  const audio = new Audio(url);
  audio.preload = 'auto';
  audio.playbackRate = opts.rate ?? 1;

  let finish: () => void;
  const done = new Promise<void>((resolve) => {
    finish = resolve;
  });
  audio.onended = () => finish();
  audio.onerror = () => finish();
  audio.play().catch(() => finish());

  return {
    done,
    stop: () => {
      audio.pause();
      audio.src = '';
      finish();
    },
    pause: () => audio.pause(),
    resume: () => {
      audio.play().catch(() => finish());
    },
    setRate: (rate) => {
      audio.playbackRate = rate;
    },
  };
}

/**
 * Markdown → texto natural para TTS: sin énfasis, links como su texto,
 * bullets con punto final (pausa), sin pipes de tabla ni emojis (Polly
 * los deletrea — el saludo de D-Talk trae 👋).
 */
export function stripMarkdownForSpeech(md: string): string {
  return md
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/~~(.*?)~~/g, '$1')
    .replace(/^\s*(?:[-*+]|\d+[.)])\s+(.*)$/gm, (_m, item: string) =>
      /[.!?…:]$/.test(item.trim()) ? item.trim() : `${item.trim()}.`
    )
    .replace(/^\s*>\s?/gm, '')
    .replace(/\|/g, ' ')
    .replace(/\p{Extended_Pictographic}/gu, '')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{2,}/g, '\n')
    .trim();
}

/**
 * Corta por fin de oración acumulando hasta `max` chars (límite billed de
 * Polly ≈3000; usamos 2500 de margen). Oración gigante → hard-split.
 */
export function splitIntoSpeechChunks(text: string, max = 2500): string[] {
  const sentences = text.match(/[^.!?\n]+[.!?…]*[\s\n]*/g) ?? [text];
  const chunks: string[] = [];
  let current = '';
  for (const sentence of sentences) {
    if (sentence.length > max) {
      if (current.trim()) chunks.push(current.trim());
      current = '';
      for (let i = 0; i < sentence.length; i += max) {
        chunks.push(sentence.slice(i, i + max).trim());
      }
      continue;
    }
    if ((current + sentence).length > max) {
      if (current.trim()) chunks.push(current.trim());
      current = sentence;
    } else {
      current += sentence;
    }
  }
  if (current.trim()) chunks.push(current.trim());
  return chunks.filter(Boolean);
}

/** Fallback si Polly falla: TTS del navegador en el idioma del chat. */
export function speakWithBrowser(text: string, lang: string): void {
  try {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    utt.lang = lang;
    utt.rate = 1.05;
    window.speechSynthesis.speak(utt);
  } catch {
    // sin TTS de navegador: silencio, nunca crash
  }
}

export function stopBrowserSpeech(): void {
  try {
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
  } catch {
    // ignorar
  }
}
